async function fetchMentionableUsers(query = '') {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());

  const url = params.toString() ? `/mentionable_users.json?${params}` : '/mentionable_users.json';
  const response = await fetch(url);
  if (!response.ok) return [];

  return response.json();
}

function filterMentionableUsers(users, query) {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return users.slice(0, 8);

  return users
    .filter((user) => user.name.toLowerCase().includes(normalized))
    .slice(0, 8);
}

function getMentionMatch(content) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!content.contains(range.startContainer)) return null;

  const { startContainer, startOffset } = range;
  if (startContainer.nodeType !== Node.TEXT_NODE) return null;

  const textBefore = startContainer.textContent.slice(0, startOffset);
  const match = textBefore.match(/@([^@\n]{0,40})$/);
  if (!match) return null;

  return {
    query: match[1],
    atOffset: startOffset - match[0].length,
    textNode: startContainer,
    range
  };
}

function hideMentionDropdown(editor) {
  const dropdown = editor.querySelector('[data-mention-dropdown]');
  if (dropdown) dropdown.remove();
  editor._mentionMatch = null;
}

function positionMentionDropdown(editor, dropdown) {
  const content = editor.querySelector('[data-rich-text-content]');
  const selection = window.getSelection();
  if (!content || !selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0).cloneRange();
  range.collapse(true);

  const marker = document.createElement('span');
  marker.textContent = '\u200b';
  range.insertNode(marker);

  const editorRect = editor.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  marker.remove();

  dropdown.style.left = `${markerRect.left - editorRect.left}px`;
  dropdown.style.top = `${markerRect.bottom - editorRect.top + 4}px`;
}

function renderMentionDropdown(editor, users, mentionMatch) {
  hideMentionDropdown(editor);
  if (!users.length) return;

  const dropdown = document.createElement('div');
  dropdown.className = 'mention-dropdown';
  dropdown.setAttribute('data-mention-dropdown', 'true');

  users.forEach((user, index) => {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'mention-dropdown__item';
    item.dataset.userId = user.id;
    item.dataset.userName = user.name;
    item.textContent = user.name;
    if (index === 0) item.classList.add('is-active');
    dropdown.appendChild(item);
  });

  editor.appendChild(dropdown);
  editor._mentionMatch = mentionMatch;
  positionMentionDropdown(editor, dropdown);
}

async function updateMentionDropdown(editor, content) {
  const mentionMatch = getMentionMatch(content);
  if (!mentionMatch) {
    hideMentionDropdown(editor);
    return;
  }

  const users = await fetchMentionableUsers(mentionMatch.query);
  const filtered = filterMentionableUsers(users, mentionMatch.query);
  renderMentionDropdown(editor, filtered, mentionMatch);
}

function insertMention(editor, content, input, user) {
  const mentionMatch = editor._mentionMatch;
  if (!mentionMatch) return;

  const { textNode, atOffset } = mentionMatch;
  const selection = window.getSelection();
  const range = document.createRange();
  const endOffset = selection.getRangeAt(0).startOffset;

  range.setStart(textNode, atOffset);
  range.setEnd(textNode, endOffset);
  range.deleteContents();

  const mention = document.createElement('span');
  mention.className = 'mention';
  mention.contentEditable = 'false';
  mention.dataset.userId = user.id;
  mention.textContent = `@${user.name}`;

  range.insertNode(mention);

  const space = document.createTextNode('\u00a0');
  mention.after(space);

  const newRange = document.createRange();
  newRange.setStartAfter(space);
  newRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(newRange);

  hideMentionDropdown(editor);
  window.syncRichTextEditors(editor, false);
  showVisitFollowUpHint(editor, user);
  content.focus();
}

function showVisitFollowUpHint(editor, user) {
  const form = editor.closest('form');
  const template = form?.dataset.followUpHint;
  if (!form || !template || !user?.name) return;

  const dateInput = form.querySelector('[name="note[visit_follow_up_date]"]');
  const dateValue = dateInput?.value;
  let message = template.replace('__NAME__', user.name);

  if (dateValue && form.dataset.followUpHintWithDate) {
    const [year, month, day] = dateValue.split('-').map(Number);
    const formatted = new Date(year, month - 1, day).toLocaleDateString(
      form.dataset.locale === 'sv' ? 'sv-SE' : 'da-DK',
      { day: 'numeric', month: 'long', year: 'numeric' }
    );
    message = form.dataset.followUpHintWithDate.replace('__NAME__', user.name).replace('__DATE__', formatted);
  }

  let hint = editor.parentElement.querySelector('[data-follow-up-hint]');
  if (!hint) {
    hint = document.createElement('p');
    hint.className = 'follow-up-mention-hint';
    hint.setAttribute('data-follow-up-hint', 'true');
    hint.setAttribute('role', 'status');
    hint.setAttribute('aria-live', 'polite');
    editor.insertAdjacentElement('afterend', hint);
  }

  hint.textContent = message;
  hint.classList.remove('is-visible');
  void hint.offsetWidth;
  hint.classList.add('is-visible');

  if (dateInput) {
    dateInput.classList.remove('is-follow-up-pulse');
    void dateInput.offsetWidth;
    dateInput.classList.add('is-follow-up-pulse');
  }

  clearTimeout(hint._hideTimer);
  hint._hideTimer = setTimeout(() => hint.classList.remove('is-visible'), 4600);
}

function moveMentionSelection(editor, direction) {
  const dropdown = editor.querySelector('[data-mention-dropdown]');
  if (!dropdown) return false;

  const items = Array.from(dropdown.querySelectorAll('.mention-dropdown__item'));
  if (!items.length) return false;

  const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
  const nextIndex = direction === 'down'
    ? (activeIndex + 1) % items.length
    : (activeIndex - 1 + items.length) % items.length;

  items.forEach((item) => item.classList.remove('is-active'));
  items[nextIndex].classList.add('is-active');
  items[nextIndex].scrollIntoView({ block: 'nearest' });
  return true;
}

function selectActiveMention(editor, content, input) {
  const dropdown = editor.querySelector('[data-mention-dropdown]');
  if (!dropdown) return false;

  const active = dropdown.querySelector('.mention-dropdown__item.is-active');
  if (!active) return false;

  insertMention(editor, content, input, {
    id: active.dataset.userId,
    name: active.dataset.userName
  });
  return true;
}

export function initMentionAutocomplete(editor, content, input) {
  content.addEventListener('input', () => {
    updateMentionDropdown(editor, content);
  });

  content.addEventListener('keydown', (event) => {
    const dropdown = editor.querySelector('[data-mention-dropdown]');
    if (!dropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveMentionSelection(editor, 'down');
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveMentionSelection(editor, 'up');
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      selectActiveMention(editor, content, input);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      hideMentionDropdown(editor);
    }
  });

  editor.addEventListener('click', (event) => {
    const item = event.target.closest('.mention-dropdown__item');
    if (!item) return;

    event.preventDefault();
    insertMention(editor, content, input, {
      id: item.dataset.userId,
      name: item.dataset.userName
    });
  });

  document.addEventListener('click', (event) => {
    if (!editor.contains(event.target)) hideMentionDropdown(editor);
  });
}
